// The frontend's view registry (lane contract C-29, R0 for the router). A lane registers its screens at import, from its
// own `views/<lane>/register.ts`, and App.tsx resolves a route through matchView before its own V1 cases, so no lane
// edits App.tsx or router.ts to add a screen. This module imports nothing from a lane: `shell/views.ts` re-exports it
// and holds the lanes' import list, so a lane's register.ts may import registerView from either module without an
// import cycle reaching a binding that is not yet initialised.

import type { ComponentType, LazyExoticComponent } from "react";
import type { Route } from "@/router";

export interface ViewProps {
  route: Route;
}

export type ViewComponent = ComponentType<ViewProps> | LazyExoticComponent<ComponentType<ViewProps>>;

export interface ViewOptions {
  /** The lane that owns the screen (B1, B2, …). */
  lane: string;
  /** Higher wins when two patterns match one route; equal specificity falls back to registration order. */
  specificity?: number | undefined;
  /**
   * The manifest tools that open the screen's landing route (any one is enough). Absent from the manifest, the bare
   * route is the §3.3 not-found screen rather than a screen over a read that will be refused (N02); a record route under
   * it carries its own seat rule. The decision is the manifest's, never a role table here.
   */
  gates?: readonly string[] | undefined;
}

export interface ViewEntry {
  readonly pattern: RegExp | string;
  readonly component: ViewComponent;
  readonly lane: string;
  readonly specificity: number;
  readonly gates: readonly string[];
  readonly order: number;
}

export interface ViewRegistry {
  registerView(pattern: RegExp | string, component: ViewComponent, opts: ViewOptions): void;
  matchView(route: Route): ViewEntry | undefined;
  entries(): readonly ViewEntry[];
}

/** A pattern's identity for the duplicate check: the view name, or the expression's source and flags. */
function patternKey(pattern: RegExp | string): string {
  return typeof pattern === "string" ? `view:${pattern}` : `re:/${pattern.source}/${pattern.flags}`;
}

/** The route's path without its query: "action-center/history" for `#action-center/history?kind=offers`. */
export function routePath(route: Route): string {
  return [route.view, ...route.parts].join("/");
}

/**
 * A string pattern names a view (the first hash segment) and matches every route under it; a RegExp is tested against
 * the route's path without its query (`requisitions/R-1040/workbench`), so a lane can claim one sub-route of a V1 view.
 */
function matches(entry: ViewEntry, route: Route): boolean {
  if (typeof entry.pattern === "string") return entry.pattern === route.view;
  entry.pattern.lastIndex = 0;
  return entry.pattern.test(routePath(route));
}

export function createViewRegistry(): ViewRegistry {
  const list: ViewEntry[] = [];
  const keys = new Set<string>();
  return {
    registerView(pattern, component, opts) {
      const key = patternKey(pattern);
      if (keys.has(key)) {
        const first = list.find((entry) => patternKey(entry.pattern) === key);
        throw new Error(`view ${String(pattern)} is registered twice (${first?.lane ?? "?"} and ${opts.lane})`);
      }
      keys.add(key);
      list.push({ pattern, component, lane: opts.lane, specificity: opts.specificity ?? 0, gates: opts.gates ?? [], order: list.length });
    },
    matchView(route) {
      let best: ViewEntry | undefined;
      for (const entry of list) {
        if (!matches(entry, route)) continue;
        if (!best || entry.specificity > best.specificity) best = entry;
      }
      return best;
    },
    entries() {
      return list;
    },
  };
}

/** The application's one registry: lanes register into it at import; App.tsx reads it. */
export const views: ViewRegistry = createViewRegistry();

export function registerView(pattern: RegExp | string, component: ViewComponent, opts: ViewOptions): void {
  views.registerView(pattern, component, opts);
}

export function matchView(route: Route): ViewEntry | undefined {
  return views.matchView(route);
}
